import React from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isAgentAppointedWithCarrier } from 'helpers/appointments/appointments';
import { isCarrierDisabledByAgencyPreferences } from 'helpers/carriers/carriers';
import AppointmentCarrierCard from 'tailwind-components/CarrierCard/CarrierCard';
import CarrierCardNoStates from 'tailwind-components/CarrierCardNoStates/CarrierCardNoStates';
import ResourceCarrierCard from 'tailwind-components/CarrierResources/CarrierCard';
import GroupedCarrierSections from 'tailwind-components/GroupedCarrierSections/GroupedCarrierSections';
import { filterCarriersVisibleInGrid } from 'helpers/carriers/carrierSectionIconLegends';
import { shouldShowOepUpdateIcon } from 'helpers/oepUpdates';
import { isSuncareContext } from 'helpers/suncare/suncare';

const SUBTITLE =
  'Please click on a carrier icon below to view more information.';

function considerStates(carrier, user) {
  return carrier.enableStateRequestsIfAppointed
    ? isAgentAppointedWithCarrier(carrier.name, user)
    : carrier.considerStates === 'Yes' &&
        (carrier.statesOffered?.length ?? 0) > 0;
}

function CarrierGrid({
  carriers,
  activeCarrierName,
  onCarrierClick,
  actionableAppointments,
  useSimpleCarrierCards
}) {
  const user = useSelector(state => state.user.zohoData);

  if (!carriers?.length) return null;

  const carriersToRender = filterCarriersVisibleInGrid(carriers, user);

  if (useSimpleCarrierCards) {
    return (
      <div className='equal-rows simple-carrier-grid grid w-full min-w-0 gap-1.5'>
        {carriersToRender.map(carrier => (
          <div
            key={carrier.name}
            className='flex min-h-[90px] min-w-0 w-full h-full'
          >
            <ResourceCarrierCard
              name={carrier.name}
              type=''
              carrierIcon={carrier.carrierIcon}
              carrierIconColor={carrier.carrierIconColor}
              active={activeCarrierName}
              setActive={({ name }) => onCarrierClick(name)}
              agencyDisabled={isCarrierDisabledByAgencyPreferences(
                carrier,
                user
              )}
              showOepIcon={shouldShowOepUpdateIcon(carrier, user)}
              className='h-full w-full'
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className='grid w-full min-w-0 grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'>
      {carriersToRender.map(carrier => {
        const useStateCard =
          considerStates(carrier, user) && !isSuncareContext(carrier.name);
        const agencyDisabled = isCarrierDisabledByAgencyPreferences(
          carrier,
          user
        );
        const carrierActionableAppts =
          actionableAppointments?.filter(
            appt => appt.carrier === carrier.name
          ) ?? [];

        const cardWrapperClass = 'min-w-0 w-full';

        const showOepIcon = shouldShowOepUpdateIcon(carrier, user);

        if (useStateCard) {
          return (
            <div key={carrier.name} className={cardWrapperClass}>
              <AppointmentCarrierCard
                name={carrier.name}
                carrierIcon={carrier.carrierIcon}
                carrierIconColor={carrier.carrierIconColor}
                active={activeCarrierName}
                setActive={onCarrierClick}
                carrierActionableAppts={carrierActionableAppts}
                agencyDisabled={agencyDisabled}
                showOepIcon={showOepIcon}
              />
            </div>
          );
        }

        return (
          <div key={carrier.name} className={cardWrapperClass}>
            <CarrierCardNoStates
              name={carrier.name}
              carrierIcon={carrier.carrierIcon}
              carrierIconColor={carrier.carrierIconColor}
              active={activeCarrierName}
              setActive={onCarrierClick}
              carrierActionableAppts={carrierActionableAppts}
              agencyDisabled={agencyDisabled}
              showOepIcon={showOepIcon}
            />
          </div>
        );
      })}
    </div>
  );
}

CarrierGrid.propTypes = {
  carriers: propTypes.arrayOf(propTypes.object),
  activeCarrierName: propTypes.string,
  onCarrierClick: propTypes.func.isRequired,
  actionableAppointments: propTypes.arrayOf(propTypes.object),
  useSimpleCarrierCards: propTypes.bool
};

export default function MainSectionCarriers({
  groupedCarriers = [],
  onCarrierClick = () => {},
  activeCarrierName = '',
  useSimpleCarrierCards = false
}) {
  const { actionableAppointments = [] } = useSelector(
    state => state.appointments
  );

  if (!groupedCarriers.length) {
    return (
      <div className='flex flex-1 items-center justify-center rounded-xl bg-white p-8'>
        <p className='text-gray-500'>No carrier groups to display.</p>
      </div>
    );
  }

  const renderGrid = carriers => (
    <CarrierGrid
      carriers={carriers}
      activeCarrierName={activeCarrierName}
      onCarrierClick={onCarrierClick}
      actionableAppointments={
        useSimpleCarrierCards ? undefined : actionableAppointments
      }
      useSimpleCarrierCards={useSimpleCarrierCards}
    />
  );

  return (
    <div className='show-scrollbar flex min-h-0 flex-1 grow flex-col gap-8 overflow-y-auto rounded-xl bg-white p-4'>
      <GroupedCarrierSections
        groupedCarriers={groupedCarriers}
        subtitle={SUBTITLE}
        filterLegendsForGrid
        wrapperClassName='flex flex-col gap-8'
        renderMainGrid={renderGrid}
        renderSubgroupGrid={renderGrid}
      />
    </div>
  );
}

MainSectionCarriers.propTypes = {
  groupedCarriers: propTypes.arrayOf(
    propTypes.shape({
      parentGroupName: propTypes.string.isRequired,
      carriersWithoutSubgroup: propTypes.arrayOf(propTypes.object).isRequired,
      subgroups: propTypes.arrayOf(
        propTypes.shape({
          groupName: propTypes.string.isRequired,
          carriers: propTypes.arrayOf(propTypes.object).isRequired
        })
      )
    })
  ),
  onCarrierClick: propTypes.func,
  activeCarrierName: propTypes.string,
  useSimpleCarrierCards: propTypes.bool
};
